import { useEffect, useMemo, useState } from 'react'
import {
  ScanSearch,
  CircleSlash,
  FileText,
  Hash,
  Ruler,
  Eraser,
  PenLine,
  Sparkles,
  Square,
  Crop,
  RotateCw,
  ShieldCheck,
  CheckCircle2,
  Wand2,
  X,
  Highlighter,
  StickyNote,
  Stamp,
  Trash2,
  Plus,
  ChevronDown,
  ChevronRight,
  BookOpen,
  ListPlus,
  Boxes,
  Image as ImageIcon,
  Globe,
  Bookmark,
} from 'lucide-react'
import {
  useProjectStore,
  findPageById,
  findDocumentById,
} from '../store/projectStore.js'
import { scanFormFields } from '../lib/pdfActions.js'
import { recognizePage, OCR_LANGUAGES } from '../lib/ocr.js'
import { renderPageToDataURL } from '../lib/pdfjs.js'
import { readOutline } from '../lib/outline.js'
import FormEditor from './FormEditor.jsx'
import SignatureModal from './SignatureModal.jsx'
import { formatBytes } from '../lib/format.js'

function Section({ title, icon: Icon, children, defaultOpen = true, right }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <section className="mt-3 rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2.5 text-left"
      >
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          {Icon && <Icon className="h-3 w-3" />}
          {title}
        </span>
        <span className="flex items-center gap-2">
          {right}
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
          )}
        </span>
      </button>
      {open && <div className="border-t border-slate-100 p-3 dark:border-slate-800">{children}</div>}
    </section>
  )
}

export default function RightSidebar({ open = false, onClose = () => {} }) {
  const documents = useProjectStore((s) => s.documents)
  const selectedDocumentId = useProjectStore((s) => s.selectedDocumentId)
  const selectedPageId = useProjectStore((s) => s.selectedPageId)
  const setProcessing = useProjectStore((s) => s.setProcessing)
  const setFormFields = useProjectStore((s) => s.setFormFields)
  const showToast = useProjectStore((s) => s.showToast)
  const clearStampsOnPage = useProjectStore((s) => s.clearStampsOnPage)
  const clearRedactionsOnPage = useProjectStore((s) => s.clearRedactionsOnPage)
  const clearCrop = useProjectStore((s) => s.clearCrop)
  const resetRotation = useProjectStore((s) => s.resetRotation)
  const clearAnnotationsOnPage = useProjectStore((s) => s.clearAnnotationsOnPage)
  const setToolMode = useProjectStore((s) => s.setToolMode)
  const setOcrText = useProjectStore((s) => s.setOcrText)
  const applyOcr = useProjectStore((s) => s.applyOcr)
  const clearOcr = useProjectStore((s) => s.clearOcr)
  const ocrLanguage = useProjectStore((s) => s.ocrLanguage)
  const setOcrLanguage = useProjectStore((s) => s.setOcrLanguage)
  const exportOptions = useProjectStore((s) => s.exportOptions)
  const setExportOption = useProjectStore((s) => s.setExportOption)
  const setExportSubOption = useProjectStore((s) => s.setExportSubOption)
  const signatures = useProjectStore((s) => s.signatures)
  const removeSignature = useProjectStore((s) => s.removeSignature)
  const annotationSettings = useProjectStore((s) => s.annotationSettings)
  const setAnnotationSettings = useProjectStore((s) => s.setAnnotationSettings)
  const formTemplates = useProjectStore((s) => s.formTemplates)
  const saveFormTemplate = useProjectStore((s) => s.saveFormTemplate)
  const applyFormTemplate = useProjectStore((s) => s.applyFormTemplate)
  const deleteFormTemplate = useProjectStore((s) => s.deleteFormTemplate)
  const splitGroups = useProjectStore((s) => s.splitGroups)
  const togglePageInSplitGroup = useProjectStore((s) => s.togglePageInSplitGroup)

  const doc = useMemo(
    () => findDocumentById(documents, selectedDocumentId),
    [documents, selectedDocumentId]
  )
  const page = useMemo(
    () => findPageById(documents, selectedPageId),
    [documents, selectedPageId]
  )

  const includedPages = useMemo(
    () => (doc ? doc.pages.filter((p) => p.included).length : 0),
    [doc]
  )

  const [ocrProgress, setOcrProgress] = useState(null)
  const [sigModalOpen, setSigModalOpen] = useState(false)
  const [outline, setOutline] = useState(null)
  const [outlineLoading, setOutlineLoading] = useState(false)

  useEffect(() => {
    setOutline(null)
  }, [doc?.id])

  const handleScanForm = async () => {
    if (!doc) return
    setProcessing(true, `Scanning "${doc.originalName}" for form fields…`)
    try {
      const fields = await scanFormFields(doc)
      setFormFields(doc.id, fields)
      if (fields.length === 0) {
        showToast('No AcroForm fields detected.', 'info')
      } else {
        showToast(`Detected ${fields.length} field${fields.length === 1 ? '' : 's'}.`, 'success')
      }
    } catch (_) {
      showToast('Form scan failed.', 'error')
    } finally {
      setProcessing(false)
    }
  }

  const handleOcrScan = async () => {
    if (!doc || !page) return
    setProcessing(true, 'Loading OCR engine…')
    setOcrProgress({ status: 'starting', progress: 0 })
    try {
      const { dataUrl } = await renderPageToDataURL(
        doc.id,
        doc.bytes,
        page.originalIndex + 1,
        { maxWidth: 1400 }
      )
      const img = new Image()
      img.src = dataUrl
      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
      })
      const result = await recognizePage(dataUrl, {
        sourceWidth: img.width,
        sourceHeight: img.height,
        language: ocrLanguage,
        onProgress: (m) => {
          setOcrProgress({
            status: m.status || 'working',
            progress: m.progress || 0,
          })
          if (m.status) setProcessing(true, `OCR · ${m.status}…`)
        },
      })
      setOcrText(page.id, result.words, ocrLanguage)
      if (result.words.length === 0) {
        showToast('No readable text detected on this page.', 'info')
      } else {
        showToast(
          `OCR found ${result.words.length} word${result.words.length === 1 ? '' : 's'}. Apply to bake as invisible text, or click words while in Redact mode to redact them.`,
          'success'
        )
      }
    } catch (err) {
      showToast(`OCR failed: ${err.message || 'unknown error'}`, 'error')
    } finally {
      setProcessing(false)
      setOcrProgress(null)
    }
  }

  const handleLoadOutline = async () => {
    if (!doc) return
    setOutlineLoading(true)
    try {
      const items = await readOutline(doc)
      setOutline(items)
      if (items.length === 0) showToast('This document has no bookmarks.', 'info')
    } catch (_) {
      showToast('Could not read outline.', 'error')
    } finally {
      setOutlineLoading(false)
    }
  }

  return (
    <aside
      className={`flex w-[90vw] max-w-[22rem] shrink-0 flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-200 ease-out
        fixed inset-y-0 right-0 top-[57px] z-40
        ${open ? 'translate-x-0' : 'translate-x-full'}
        md:static md:top-0 md:z-0 md:w-80 md:translate-x-0 md:shadow-none
        dark:border-slate-800 dark:bg-slate-900`}
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
            <Sparkles className="h-3.5 w-3.5" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Smart inspector</h2>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Forms · OCR · Annotations · Export</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 md:hidden dark:hover:bg-slate-800"
          aria-label="Close inspector"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {!doc && (
          <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
            Select a document or upload a PDF / image to inspect.
          </div>
        )}

        {doc && (
          <>
            {/* Document */}
            <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-soft dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-start gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  {doc.sourceKind === 'image' ? (
                    <ImageIcon className="h-4 w-4" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100" title={doc.originalName}>
                    {doc.originalName}
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    {doc.pages.length} pages · {formatBytes(doc.size)} · {includedPages} included
                  </p>
                </div>
              </div>
            </section>

            {/* Selected page */}
            {page && (
              <Section title="Selected page" icon={Hash} defaultOpen>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-md bg-slate-50 p-2 dark:bg-slate-800">
                    <p className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
                      <Hash className="h-3 w-3" /> Page
                    </p>
                    <p className="font-semibold text-slate-800 dark:text-slate-100">
                      {page.originalIndex + 1} of {doc.pages.length}
                    </p>
                  </div>
                  <div className="rounded-md bg-slate-50 p-2 dark:bg-slate-800">
                    <p className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
                      <Ruler className="h-3 w-3" /> Size
                    </p>
                    <p className="font-semibold text-slate-800 dark:text-slate-100">
                      {Math.round(page.width)} × {Math.round(page.height)} pt
                    </p>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-1.5">
                  <ToolBtn icon={Stamp} label="Stamp" onClick={() => setToolMode('stamp')} />
                  <ToolBtn icon={Square} label="Redact" onClick={() => setToolMode('redact')} />
                  <ToolBtn icon={Crop} label="Crop" onClick={() => setToolMode('crop')} />
                  <ToolBtn icon={Highlighter} label="Highlight" onClick={() => setToolMode('highlight')} />
                  <ToolBtn icon={PenLine} label="Ink" onClick={() => setToolMode('ink')} />
                  <ToolBtn icon={StickyNote} label="Sticky" onClick={() => setToolMode('sticky')} />
                  <ToolBtn icon={Sparkles} label="Signature" onClick={() => setToolMode('signature')} />
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(page.stamps || []).length > 0 && (
                    <ClearChip onClick={() => clearStampsOnPage(page.id)}>Clear stamps</ClearChip>
                  )}
                  {(page.redactions || []).length > 0 && (
                    <ClearChip onClick={() => clearRedactionsOnPage(page.id)}>Clear redactions</ClearChip>
                  )}
                  {(page.annotations || []).length > 0 && (
                    <ClearChip onClick={() => clearAnnotationsOnPage(page.id)}>Clear annotations</ClearChip>
                  )}
                  {page.crop && <ClearChip onClick={() => clearCrop(page.id)}>Clear crop</ClearChip>}
                  {(page.rotation || 0) !== 0 && (
                    <ClearChip onClick={() => resetRotation(page.id)}>Reset rotation</ClearChip>
                  )}
                </div>
              </Section>
            )}

            {/* OCR */}
            {page && (
              <Section
                title="OCR — searchable text"
                icon={ScanSearch}
                right={
                  <select
                    value={ocrLanguage}
                    onChange={(e) => setOcrLanguage(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border border-slate-200 bg-white px-1 py-0.5 text-[10px] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    title="OCR language"
                  >
                    {OCR_LANGUAGES.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                }
              >
                <button
                  type="button"
                  onClick={handleOcrScan}
                  className="btn-primary h-8 w-full text-xs"
                >
                  <ScanSearch className="h-3.5 w-3.5" />
                  Scan for text
                </button>

                {ocrProgress && (
                  <div className="mt-2">
                    <p className="text-[11px] text-slate-500">
                      {ocrProgress.status}… {Math.round((ocrProgress.progress || 0) * 100)}%
                    </p>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                      <div
                        className="h-full bg-brand-500 transition-all"
                        style={{ width: `${Math.round((ocrProgress.progress || 0) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {page.ocrText && page.ocrText.length > 0 && (
                  <div className="mt-2 space-y-2">
                    <div className="rounded-md bg-slate-50 p-2 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      <p>
                        <span className="font-semibold text-slate-800 dark:text-slate-100">
                          {page.ocrText.length}
                        </span>{' '}
                        word{page.ocrText.length === 1 ? '' : 's'} detected ({page.ocrLanguage}).
                      </p>
                      {page.ocrApplied && (
                        <p className="mt-1 inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                          <CheckCircle2 className="h-3 w-3" />
                          Applied — exported as invisible searchable text.
                        </p>
                      )}
                      <p className="mt-1 text-[10px] text-slate-500">
                        Tip: switch to <strong>Redact</strong> and click any word to redact it.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {!page.ocrApplied ? (
                        <button
                          onClick={() => applyOcr(page.id, true)}
                          className="btn-primary h-8 flex-1 text-xs"
                        >
                          <Wand2 className="h-3.5 w-3.5" /> Apply OCR
                        </button>
                      ) : (
                        <button
                          onClick={() => applyOcr(page.id, false)}
                          className="btn-secondary h-8 flex-1 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                        >
                          Unapply
                        </button>
                      )}
                      <button
                        onClick={() => clearOcr(page.id)}
                        className="btn-ghost h-8 px-3 text-xs text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                      >
                        <Eraser className="h-3.5 w-3.5" /> Clear
                      </button>
                    </div>
                  </div>
                )}
              </Section>
            )}

            {/* Form fields */}
            <Section
              title="Form fields"
              icon={ListPlus}
              defaultOpen={!!doc.formScanned}
              right={
                !doc.formScanned && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleScanForm()
                    }}
                    className="btn-secondary h-6 px-1.5 text-[10px] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    <ScanSearch className="h-3 w-3" />
                    Scan
                  </button>
                )
              }
            >
              {!doc.formScanned && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Run a scan to detect AcroForm fields.
                </p>
              )}
              {doc.formScanned && doc.formFields?.length === 0 && (
                <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2.5 text-[11px] text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                  <CircleSlash className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <p>No interactive form fields found.</p>
                </div>
              )}
              {doc.formScanned && doc.formFields && doc.formFields.length > 0 && (
                <>
                  <FormEditor doc={doc} />
                  <div className="mt-3 border-t border-slate-100 pt-3 dark:border-slate-800">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Templates
                    </p>
                    <button
                      onClick={() => {
                        const name = window.prompt('Template name:', `Template ${formTemplates.length + 1}`)
                        if (name) saveFormTemplate(name)
                      }}
                      className="btn-secondary mt-1 h-7 w-full text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    >
                      <Plus className="h-3 w-3" /> Save current values as template
                    </button>
                    {formTemplates.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {formTemplates.map((t) => (
                          <li
                            key={t.id}
                            className="flex items-center gap-2 rounded-md border border-slate-200 px-2 py-1.5 text-xs dark:border-slate-700"
                          >
                            <span className="flex-1 truncate text-slate-700 dark:text-slate-200" title={t.name}>
                              {t.name}
                            </span>
                            <button
                              onClick={() => applyFormTemplate(t.id)}
                              className="text-[10px] font-medium text-brand-700 hover:underline dark:text-brand-200"
                            >
                              Apply
                            </button>
                            <button
                              onClick={() => deleteFormTemplate(t.id)}
                              className="rounded p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/30"
                              aria-label={`Delete ${t.name}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </Section>

            {/* Signatures */}
            <Section title="Signatures" icon={Sparkles} defaultOpen={false}>
              <button
                onClick={() => setSigModalOpen(true)}
                className="btn-secondary h-8 w-full text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <Plus className="h-3 w-3" /> Add signature
              </button>
              {signatures.length === 0 ? (
                <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                  Add a signature, then pick the Signature tool in the toolbar and drag a region on a page.
                </p>
              ) : (
                <ul className="mt-2 space-y-1.5">
                  {signatures.map((s) => (
                    <li
                      key={s.id}
                      className={`flex items-center gap-2 rounded-md border px-2 py-1.5 ${
                        annotationSettings.signatureId === s.id
                          ? 'border-brand-300 bg-brand-50/60 dark:border-brand-700 dark:bg-brand-900/30'
                          : 'border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      <img src={s.dataUrl} alt={s.name} className="h-6 max-w-[64px] object-contain" />
                      <span className="flex-1 truncate text-xs text-slate-700 dark:text-slate-200" title={s.name}>
                        {s.name}
                      </span>
                      <button
                        onClick={() => setAnnotationSettings({ signatureId: s.id })}
                        className="text-[10px] font-medium text-brand-700 hover:underline dark:text-brand-200"
                      >
                        Use
                      </button>
                      <button
                        onClick={() => removeSignature(s.id)}
                        className="rounded p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/30"
                        aria-label="Delete signature"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* Split groups */}
            {page && splitGroups.length > 0 && (
              <Section title="Split groups" icon={Boxes} defaultOpen={false}>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Toggle which groups contain this page. Used when exporting in <em>split</em> mode.
                </p>
                <ul className="mt-2 space-y-1">
                  {splitGroups.map((g) => {
                    const inGroup = g.pageIds.includes(page.id)
                    return (
                      <li key={g.id}>
                        <label className="flex items-center gap-2 rounded-md border border-slate-200 px-2 py-1.5 text-xs dark:border-slate-700">
                          <input
                            type="checkbox"
                            checked={inGroup}
                            onChange={() => togglePageInSplitGroup(g.id, page.id)}
                            className="h-3.5 w-3.5 accent-brand-600"
                          />
                          <span className="flex-1 truncate text-slate-700 dark:text-slate-200">{g.name}</span>
                          <span className="text-[10px] text-slate-400">{g.pageIds.length}</span>
                        </label>
                      </li>
                    )
                  })}
                </ul>
              </Section>
            )}

            {/* Bookmarks / Outline */}
            <Section title="Bookmarks" icon={Bookmark} defaultOpen={false}>
              <button
                onClick={handleLoadOutline}
                disabled={outlineLoading}
                className="btn-secondary h-7 w-full text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <BookOpen className="h-3 w-3" />
                {outlineLoading ? 'Reading…' : 'Read outline'}
              </button>
              {outline !== null && (
                <>
                  {outline.length === 0 ? (
                    <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                      No bookmarks in this document.
                    </p>
                  ) : (
                    <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs">
                      {outline.map((entry, i) => (
                        <li
                          key={i}
                          className="rounded px-1.5 py-0.5 text-slate-700 dark:text-slate-300"
                          style={{ paddingLeft: 6 + entry.depth * 12 }}
                          title={entry.title}
                        >
                          • {entry.title}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                    Bookmarks are stripped when <strong>Scrub metadata</strong> is enabled (a privacy-safe default).
                  </p>
                </>
              )}
            </Section>

            {/* Export options */}
            <Section title="Export options" icon={Wand2} defaultOpen={false}>
              <div className="mb-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Mode</p>
                <div className="mt-1 grid grid-cols-3 gap-1">
                  {[
                    { id: 'merged', label: 'Merged' },
                    { id: 'split', label: 'Split' },
                    { id: 'images', label: 'Images' },
                  ].map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setExportOption({ mode: m.id })}
                      className={`rounded-md border px-2 py-1.5 text-[11px] transition ${
                        exportOptions.mode === m.id
                          ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-700 dark:bg-brand-900/30 dark:text-brand-200'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {exportOptions.mode === 'images' && (
                <div className="mb-3 space-y-2">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Format
                    </p>
                    <div className="mt-1 grid grid-cols-2 gap-1">
                      {['png', 'jpg'].map((f) => (
                        <button
                          key={f}
                          onClick={() => setExportOption({ imageFormat: f })}
                          className={`rounded-md border px-2 py-1 text-[11px] uppercase ${
                            exportOptions.imageFormat === f
                              ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-700 dark:bg-brand-900/30 dark:text-brand-200'
                              : 'border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'
                          }`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="block text-[11px] text-slate-600 dark:text-slate-300">
                    DPI
                    <input
                      type="number"
                      min={72}
                      max={400}
                      value={exportOptions.imageDpi}
                      onChange={(e) =>
                        setExportOption({
                          imageDpi: Math.max(72, Math.min(400, Number(e.target.value) || 144)),
                        })
                      }
                      className="input mt-1 h-7 text-xs dark:bg-slate-800 dark:text-slate-100"
                    />
                  </label>
                </div>
              )}

              {exportOptions.mode === 'merged' && (
                <div className="mb-3 space-y-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Compression
                  </p>
                  <div className="grid grid-cols-3 gap-1">
                    {[
                      { id: 'normal', label: 'Normal' },
                      { id: 'compact', label: 'Compact' },
                      { id: 'rasterize', label: 'Rasterize' },
                    ].map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setExportOption({ compression: c.id })}
                        className={`rounded-md border px-2 py-1.5 text-[11px] ${
                          exportOptions.compression === c.id
                            ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-700 dark:bg-brand-900/30 dark:text-brand-200'
                            : 'border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'
                        }`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                  {exportOptions.compression === 'rasterize' && (
                    <label className="block text-[11px] text-slate-600 dark:text-slate-300">
                      Rasterize DPI
                      <input
                        type="number"
                        min={72}
                        max={300}
                        value={exportOptions.rasterizeDpi}
                        onChange={(e) =>
                          setExportOption({
                            rasterizeDpi: Math.max(72, Math.min(300, Number(e.target.value) || 110)),
                          })
                        }
                        className="input mt-1 h-7 text-xs dark:bg-slate-800 dark:text-slate-100"
                      />
                    </label>
                  )}
                </div>
              )}

              {/* Bates */}
              <div className="mb-3 rounded-md border border-slate-200 p-2 dark:border-slate-700">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={exportOptions.bates.enabled}
                    onChange={(e) => setExportSubOption('bates', { enabled: e.target.checked })}
                    className="h-3.5 w-3.5 accent-brand-600"
                  />
                  <span className="font-medium text-slate-700 dark:text-slate-200">Bates page numbering</span>
                </label>
                {exportOptions.bates.enabled && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Prefix
                      <input
                        value={exportOptions.bates.prefix}
                        onChange={(e) => setExportSubOption('bates', { prefix: e.target.value })}
                        placeholder="e.g. EX-"
                        className="input mt-1 h-7 text-xs dark:bg-slate-800 dark:text-slate-100"
                      />
                    </label>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Start
                      <input
                        type="number"
                        value={exportOptions.bates.start}
                        onChange={(e) =>
                          setExportSubOption('bates', { start: Number(e.target.value) || 1 })
                        }
                        className="input mt-1 h-7 text-xs dark:bg-slate-800 dark:text-slate-100"
                      />
                    </label>
                    <label className="col-span-2 text-[10px] uppercase tracking-wider text-slate-500">
                      Position
                      <select
                        value={exportOptions.bates.position}
                        onChange={(e) => setExportSubOption('bates', { position: e.target.value })}
                        className="input mt-1 h-7 text-xs dark:bg-slate-800 dark:text-slate-100"
                      >
                        <option value="bottom-left">Bottom left</option>
                        <option value="bottom-center">Bottom center</option>
                        <option value="bottom-right">Bottom right</option>
                        <option value="top-left">Top left</option>
                        <option value="top-center">Top center</option>
                        <option value="top-right">Top right</option>
                      </select>
                    </label>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Size
                      <input
                        type="number"
                        min={6}
                        max={48}
                        value={exportOptions.bates.size}
                        onChange={(e) =>
                          setExportSubOption('bates', { size: Number(e.target.value) || 10 })
                        }
                        className="input mt-1 h-7 text-xs dark:bg-slate-800 dark:text-slate-100"
                      />
                    </label>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Color
                      <input
                        type="color"
                        value={exportOptions.bates.color}
                        onChange={(e) => setExportSubOption('bates', { color: e.target.value })}
                        className="mt-1 h-7 w-full cursor-pointer rounded border border-slate-200 dark:border-slate-700"
                      />
                    </label>
                  </div>
                )}
              </div>

              {/* Watermark */}
              <div className="mb-3 rounded-md border border-slate-200 p-2 dark:border-slate-700">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={exportOptions.watermark.enabled}
                    onChange={(e) => setExportSubOption('watermark', { enabled: e.target.checked })}
                    className="h-3.5 w-3.5 accent-brand-600"
                  />
                  <span className="font-medium text-slate-700 dark:text-slate-200">Diagonal watermark</span>
                </label>
                {exportOptions.watermark.enabled && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <label className="col-span-2 text-[10px] uppercase tracking-wider text-slate-500">
                      Text
                      <input
                        value={exportOptions.watermark.text}
                        onChange={(e) => setExportSubOption('watermark', { text: e.target.value })}
                        placeholder="e.g. DRAFT"
                        className="input mt-1 h-7 text-xs dark:bg-slate-800 dark:text-slate-100"
                      />
                    </label>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Size
                      <input
                        type="number"
                        min={8}
                        max={200}
                        value={exportOptions.watermark.size}
                        onChange={(e) =>
                          setExportSubOption('watermark', { size: Number(e.target.value) || 64 })
                        }
                        className="input mt-1 h-7 text-xs dark:bg-slate-800 dark:text-slate-100"
                      />
                    </label>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Angle°
                      <input
                        type="number"
                        min={-90}
                        max={90}
                        value={exportOptions.watermark.angle}
                        onChange={(e) =>
                          setExportSubOption('watermark', { angle: Number(e.target.value) || 0 })
                        }
                        className="input mt-1 h-7 text-xs dark:bg-slate-800 dark:text-slate-100"
                      />
                    </label>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Color
                      <input
                        type="color"
                        value={exportOptions.watermark.color}
                        onChange={(e) => setExportSubOption('watermark', { color: e.target.value })}
                        className="mt-1 h-7 w-full cursor-pointer rounded border border-slate-200 dark:border-slate-700"
                      />
                    </label>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Opacity
                      <input
                        type="number"
                        step="0.05"
                        min={0.05}
                        max={1}
                        value={exportOptions.watermark.opacity}
                        onChange={(e) =>
                          setExportSubOption('watermark', {
                            opacity: Math.max(0.05, Math.min(1, Number(e.target.value) || 0.2)),
                          })
                        }
                        className="input mt-1 h-7 text-xs dark:bg-slate-800 dark:text-slate-100"
                      />
                    </label>
                  </div>
                )}
              </div>
            </Section>

            {/* Privacy */}
            <Section title="Privacy" icon={ShieldCheck} defaultOpen={false}>
              <label className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={exportOptions.scrubMetadata}
                  onChange={(e) => setExportOption({ scrubMetadata: e.target.checked })}
                  className="mt-0.5 h-3.5 w-3.5 accent-brand-600"
                />
                <span>
                  <span className="block font-medium">Scrub metadata on export</span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    Strips Title, Author, Subject, Keywords, Creator, Producer, dates, and any document outline.
                  </span>
                </span>
              </label>
            </Section>
          </>
        )}
      </div>

      <div className="border-t border-slate-200 bg-slate-50/60 px-4 py-3 text-[11px] text-slate-500 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
        Local-first · pdf-lib · pdfjs · tesseract.js · jszip
      </div>

      {sigModalOpen && <SignatureModal onClose={() => setSigModalOpen(false)} />}
    </aside>
  )
}

function ToolBtn({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
    >
      <Icon className="h-3 w-3" />
      {label}
    </button>
  )
}

function ClearChip({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-rose-50 hover:text-rose-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-rose-900/30 dark:hover:text-rose-200"
    >
      <Eraser className="h-2.5 w-2.5" />
      {children}
    </button>
  )
}
